import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { RealtimeClient, type RealtimeEvent, type RealtimeStatus } from '@micarahr/api-client';
import { EVENT_NAMES } from '@micarahr/event-contracts';
import { apiSession, onSessionEnded } from '../services/api-client';
import { env } from '../config/env';
import { queryKeys } from './query';

/**
 * Realtime, wired to the cache.
 *
 * The rule the client already states and this file has to honour: **an event
 * is a hint that something changed, never the change itself.** Nothing here
 * writes a payload into the cache. Each event marks queries stale and the
 * ordinary fetch path re-reads the authoritative record, which is what stops a
 * dropped frame producing a screen that quietly disagrees with the database —
 * the worst failure a realtime feature has, because nothing looks broken.
 *
 * That also makes delivery guarantees cheap. Events arrive at-least-once and a
 * reconnect replays them; invalidating the same query twice costs one refetch,
 * whereas applying the same payload twice would double a counter.
 */

interface RealtimeContextValue {
  status: RealtimeStatus;
  /** Events seen this session, newest first. Diagnostics only, capped. */
  recent: RealtimeEvent[];
}

const RealtimeContext = createContext<RealtimeContextValue>({
  status: 'disabled',
  recent: [],
});

export function useRealtime(): RealtimeContextValue {
  return useContext(RealtimeContext);
}

/**
 * What each event invalidates.
 *
 * Deliberately written out rather than derived from the event name. A rule
 * like "invalidate the module the event is named after" reads well and is
 * wrong in both directions: a leave approval changes the approvals queue, the
 * dashboard and the employee's balance, none of which are called "leave"; and
 * a payroll status change must not invalidate every payroll query on every
 * screen, because recalculating a 4,000-line run to update a status chip is
 * how a status chip takes ten seconds.
 *
 * An event with no entry is ignored. That is the right default — an unknown
 * event from a newer server should do nothing rather than refetch everything.
 */
function affectedKeys(event: RealtimeEvent): readonly (readonly unknown[])[] {
  const keys = queryKeys;

  switch (event.name) {
    case EVENT_NAMES.LEAVE_REQUEST_SUBMITTED:
      return [keys.leave.all(), keys.approvals.all(), keys.dashboard.attention()];

    case EVENT_NAMES.OVERTIME_REQUEST_SUBMITTED:
      return [keys.overtime.all(), keys.approvals.all(), keys.dashboard.attention()];

    case EVENT_NAMES.ATTENDANCE_CORRECTION_SUBMITTED:
      return [keys.corrections.all(), keys.approvals.all(), keys.dashboard.attention()];

    /*
     * A decision taken anywhere else — another tab, another reviewer, the
     * manager's app — takes the request out of the queue. Without these the
     * Approvals badge kept counting it until its cache went stale, reading 8
     * over a list of 7.
     */
    case EVENT_NAMES.LEAVE_REQUEST_APPROVED:
    case EVENT_NAMES.LEAVE_REQUEST_REJECTED:
    case EVENT_NAMES.LEAVE_REQUEST_CANCELLED:
      return [keys.leave.all(), keys.approvals.all(), keys.dashboard.attention()];

    case EVENT_NAMES.OVERTIME_REQUEST_APPROVED:
    case EVENT_NAMES.OVERTIME_REQUEST_REJECTED:
    case EVENT_NAMES.OVERTIME_REQUEST_CANCELLED:
      return [keys.overtime.all(), keys.approvals.all(), keys.dashboard.attention()];

    case EVENT_NAMES.ATTENDANCE_CORRECTION_APPROVED:
    case EVENT_NAMES.ATTENDANCE_CORRECTION_REJECTED:
      return [
        keys.corrections.all(),
        keys.attendance.all(),
        keys.approvals.all(),
        keys.dashboard.attention(),
      ];

    case EVENT_NAMES.ATTENDANCE_RECORD_CORRECTED:
      return [
        keys.attendance.all(),
        keys.corrections.all(),
        keys.dashboard.attendanceToday(),
        keys.dashboard.attention(),
      ];

    case EVENT_NAMES.EMPLOYEE_CREATED:
      return [
        keys.employees.all(),
        keys.dashboard.workforce(),
        keys.organization.departmentOverview(),
        keys.reports.workforce(),
      ];

    /*
     * A status change moves the chip and the available actions, not the lines.
     * The run's own key stays valid until a calculation says otherwise.
     */
    case EVENT_NAMES.PAYROLL_PERIOD_CREATED:
    case EVENT_NAMES.PAYROLL_PERIOD_STATUS_CHANGED:
      return [keys.payroll.runs(), keys.dashboard.attention()];

    case EVENT_NAMES.PAYROLL_CALCULATION_COMPLETED:
      // This one does change the figures, so the whole module goes.
      return [keys.payroll.all(), keys.reports.all(), keys.dashboard.attention()];

    case EVENT_NAMES.PAYSLIP_PUBLISHED:
      return [keys.payroll.runs(), keys.notifications.all()];

    case EVENT_NAMES.TIMESHEET_PERIOD_LOCKED:
      return [keys.timesheets.all(), keys.dashboard.attention()];

    case EVENT_NAMES.ANNOUNCEMENT_PUBLISHED:
      return [keys.announcements.all(), keys.notifications.all()];

    case EVENT_NAMES.POLICY_PUBLISHED:
      return [keys.policies.all(), keys.notifications.all()];

    case EVENT_NAMES.NOTIFICATION_CREATED:
      return [keys.notifications.all()];

    default:
      return [];
  }
}

/** The map, for the test that checks it. Not used by the provider. */
export const affectedKeysForTest = affectedKeys;

function applyEvent(queryClient: QueryClient, event: RealtimeEvent): void {
  const keys = affectedKeys(event);
  for (const key of keys) {
    void queryClient.invalidateQueries({ queryKey: [...key] });
  }
  /*
   * Most of what the server announces also notifies somebody — a request
   * decided, a payslip published — so the bell's count is re-read with it.
   * One count query, and the bell updates without waiting for its poll.
   */
  if (keys.length > 0) {
    void queryClient.invalidateQueries({ queryKey: [...queryKeys.notifications.unread()] });
  }
}

const RECENT_LIMIT = 50;

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>('disabled');
  const [recent, setRecent] = useState<RealtimeEvent[]>([]);

  // Read once: `env()` is validated at startup and cannot change afterwards,
  // and putting it in the effect's dependencies would reconnect on every
  // render that happens to produce a new object.
  const config = useMemo(() => env(), []);
  const clientRef = useRef<RealtimeClient | null>(null);

  useEffect(() => {
    if (config.dataSource !== 'api' || !config.realtimeEnabled) {
      setStatus('disabled');
      return;
    }

    const client = new RealtimeClient({
      url: config.wsUrl,
      /*
       * Read fresh on every connect, not captured.
       *
       * A reconnect after a token refresh must present the new token; a
       * captured one is stale exactly when the socket needs it most, and the
       * server would answer 4401 and stop retrying.
       */
      getAccessToken: () => apiSession().getAccessTokenForSocket(),
      onStatusChange: setStatus,
      onEvent: (event) => {
        applyEvent(queryClient, event);
        setRecent((previous) => [event, ...previous].slice(0, RECENT_LIMIT));
      },
    });

    clientRef.current = client;
    void client.connect();

    // Signing out closes the socket. A live socket on a dead session is a
    // socket the server will refuse anyway, and reconnect attempts against a
    // revoked token look like an attack in the logs.
    const stopListening = onSessionEnded(() => {
      client.disconnect();
      setRecent([]);
    });

    return () => {
      stopListening();
      client.disconnect();
      clientRef.current = null;
    };
  }, [config, queryClient]);

  const value = useMemo(() => ({ status, recent }), [status, recent]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
